import { Component, Inject, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import {
  ActivityFileTypeEnum,
  ReviewStatInterface,
} from '@sport-activities/sdk';
import { format } from 'date-fns';
import { BadgeVariantEnum } from '~/component/badge/enums/variant.enum';
import { Activity } from '~/core/model/activity.model';
import { Timeslot } from '~/core/model/timeslot.model';
import { ActivityService } from '~/core/services/activity/activity.service';
import { SportsService } from '~/core/services/sports/sports.service';
import { LocaleService } from '~/i18n/services/locale.service';
import { ActivityMediaResolutionEnum } from '~/organizer/shared/enums/media-resolution.enum';
import { RatingSizeEnum } from '~/component/vtmn-rating/enums/RatingSize.enum';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';
import { ToggleService } from '~/toggles/services/toggle.service';
import { OrganizationService } from '~/core/services/organization/organization.service';
import { Subscription } from 'rxjs';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { APP_STORAGE } from '~/app.token';
import isBulgaria from '~/core/helpers/country/country.helper';

@Component({
  selector: 'app-attendee-live-card',
  templateUrl: './live-card.component.html',
  styleUrls: ['./live-card.component.scss'],
})
export class AttendeeLiveCardComponent implements OnInit {
  @Input() public activity: Activity = new Activity({});
  @Input() public isMobileDevice = false;
  @Input() public timeslot: Timeslot = new Timeslot({});
  @Input() public organizationName: string;

  private readonly subscriptions = new Subscription();

  public reviewsStats: ReviewStatInterface;
  public badgeVariant = BadgeVariantEnum.GREY_LIGHT;
  public buttonIcon = VtmnPlayIconTypeEnum.ArrowRight;
  public priceBadgeVariant = BadgeVariantEnum.YELLOW;
  public readonly ratingSizeEnum = RatingSizeEnum;
  public readonly toggleNameEnum = ToggleNameEnum;

  constructor(
    @Inject(APP_STORAGE) private readonly storage: Storage,
    private readonly sportsService: SportsService,
    private readonly localeService: LocaleService,
    private readonly localizeRouterService: LocalizeRouterService,
    private readonly activityService: ActivityService,
    private readonly router: Router,
    private readonly toggleService: ToggleService,
    private readonly organizationService: OrganizationService
  ) {}

  public ngOnInit(): void {
    this.organizationService
      .checkOrganizationName(this.activity?.organizationIdentifier)
      .then((name) => {
        if (name !== '') {
          this.organizationName = name;
        } else {
          this.organizationService
            .getOrganizationById(this.activity?.organizationIdentifier)
            .subscribe((organization) => {
              const storageKey =
                'organization_' + this.activity?.organizationIdentifier;
              const storageDateKey =
                'organization_' +
                this.activity?.organizationIdentifier +
                '_date';

              this.organizationName = organization.name;
              this.storage?.setItem(storageKey, organization.name);
              this.storage?.setItem(
                storageDateKey,
                JSON.stringify(new Date().getTime())
              );
            });
        }
      });

    if (this.isActivityCardRating) {
      this.activityService
        .getActivityReviewsStats(this.activity.sku, { disableRetry: true })
        .subscribe((reviewsStats) => {
          this.reviewsStats = reviewsStats;
        });
    }
  }

  public get isBg(): boolean {
    return isBulgaria();
  }

  public get formattedDuration(): string {
    return this.timeslot.formattedDuration({
      locale: this.localeService.dateLocale,
      format: ['hours', 'minutes'],
    });
  }

  public get hasAgeRange(): boolean {
    return Boolean(this.activity.typicalAgeRange);
  }

  public get hasDuration(): boolean {
    return Boolean(this.timeslot.startDate && this.timeslot.endDate);
  }

  public get hasLevel(): boolean {
    return Boolean(this.activity.proficiencyLevel);
  }

  public get hasPrice(): boolean {
    return Boolean(this.activity.startingPrice);
  }

  public get picture(): string {
    return (
      this.activity.getMediaUrlByType(
        'MAIN_MEDIA' as ActivityFileTypeEnum,
        ActivityMediaResolutionEnum.ICON
      ) || this.activity.picture
    );
  }

  public get role(): string {
    return this.isMobileDevice ? 'link' : null;
  }

  public get sportsName(): string {
    if (!this.activity.sports) {
      return '';
    }

    return this.activity.sports
      .map((id) => this.sportsService.getSport(id)?.name)
      .filter((sport) => sport)
      .join(', ');
  }

  public get startHour(): string {
    if (!this.timeslot.startDate) {
      return '';
    }

    return format(new Date(this.timeslot.startDate), 'HH:mm', {
      locale: this.localeService.dateLocale,
    });
  }

  public get tabIndex(): number {
    return this.isMobileDevice ? 0 : null;
  }

  public get isActivityCardRating(): boolean {
    return this.toggleService.isEnabled(ToggleNameEnum.ActivityCardRating);
  }

  public goToActivityDetail(sku: number | string): Promise<boolean> {
    if (!this.isMobileDevice) {
      return;
    }

    const translatedPath = this.localizeRouterService.translateRoute([
      '/attendee.show-live-activity',
      sku,
    ]) as [];

    return this.router.navigate(translatedPath);
  }
}
